import { useState } from 'react'
import API from '../API/API'
import { useAlerts } from '../contexts/AlertContext'
import logger from '../utils/logger'
import { HelmetInfo, HelmetLookupFormData, Organization, TransferSuccess, ConvertibleValidation } from '../types/helmetLookup'

interface UseHelmetTransferParams {
  helmetInfo: HelmetInfo | null
  organizations: Organization[]
  availableTargetTeams: any[]
  convertibleValidation: ConvertibleValidation | null
  setError: (error: string) => void
  resetLookupState: () => void
  resetForm: () => void
}

export const useHelmetTransfer = ({
  helmetInfo,
  organizations,
  availableTargetTeams,
  convertibleValidation,
  setError,
  resetLookupState,
  resetForm
}: UseHelmetTransferParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [transferSuccess, setTransferSuccess] = useState<TransferSuccess | null>(null)

  const { addAlert } = useAlerts()

  const handleTransferSubmit = async (data: HelmetLookupFormData) => {
    if (!helmetInfo) {
      setError('No helmet selected for transfer')
      logger.error('Transfer failed: No helmetInfo', { helmetInfo });
      return;
    }

    if (!data.confirmTransfer) {
      setError('Please confirm the transfer by checking the confirmation box');
      logger.error('Transfer failed: Confirmation not checked', { data });
      return;
    }

    if (!data.targetOrgID) {
      setError('Please select a target organization');
      logger.error('Transfer failed: No targetOrgID', { data });
      return;
    }


    // Use the selectedHelmetInfo from the lookup/disambiguation step for all extended helmet info
    // This ensures the update uses the user's selected data
    try {
      setIsSubmitting(true);

      // Save the original playerUnitSerialString before transfer
      const originalPlayerUnitSerialString = helmetInfo.playerUnitSerialString;

      const isConvertibleHelmet = helmetInfo.features?.toLowerCase() === 'convertible' && convertibleValidation?.isValid && convertibleValidation?.requiredPlayer

      // Step 1: Transfer player unit or assign convertible
      if (isConvertibleHelmet && convertibleValidation?.requiredPlayer) {
        // Convertible: assign via PutPlayersTeamsEquipment to matching RIDDELL player
        try {
          if (!helmetInfo.playerUnitID) {
            const missingIdError = 'Convertible transfer requires playerUnitID, but it was not available in lookup data.'
            logger.error(missingIdError, { helmetInfo })
            setError(missingIdError)
            addAlert('error', missingIdError)
            return
          }

          const equipmentPayload = {
            teamID: data.targetTeamID ? parseInt(data.targetTeamID) : 0,
            playerID: convertibleValidation.requiredPlayer.playerID,
            firstName: convertibleValidation.requiredPlayer.firstName,
            lastName: convertibleValidation.requiredPlayer.lastName,
            jerseyNumber: convertibleValidation.requiredPlayer.jerseyNumber,
            positionID: convertibleValidation.requiredPlayer.positionID || 0,
            playerUnitID: helmetInfo.playerUnitID
          }
          logger.info('Convertible helmet assignment payload', { equipmentPayload, helmetInfo })
          await API.PutPlayersTeamsEquipment(equipmentPayload)
        } catch (err: any) {
          const equipmentError = 'PlayersTeamsEquipment assignment failed: ' + (err?.message || err)
          logger.error(equipmentError, { error: err, helmetInfo })
          setError(equipmentError)
          addAlert('error', equipmentError)
          return
        }
      } else {
        // Non-convertible: transfer player unit to new team
        let transferError = '';
        try {
          const transferPayload = {
            playerUnitSerialString: helmetInfo.playerUnitSerialString,
            playerUnitFirmwareVersion: helmetInfo.playerUnitFirmwareVersionSemVer || '',
            playerUnitHardwareNumber: helmetInfo.playerUnitHardwareNumber,
            playerUnitAddress: 0,
            slot: 0,
            superFrame: 0,
            teamID: data.targetTeamID ? parseInt(data.targetTeamID) : 0,
          };
          logger.info('Transfer payload', { transferPayload, helmetInfo });
          if (!transferPayload.playerUnitSerialString || !transferPayload.teamID) {
            logger.error('Invalid transfer payload', { transferPayload });
            setError('Invalid transfer payload');
            return;
          }
          await API.PostPlayerUnitToTeam(transferPayload);
        } catch (err: any) {
          transferError = 'PlayerUnitToTeam transfer failed: ' + (err?.message || err);
          logger.error(transferError, { error: err, helmetInfo });
          setError(transferError);
          addAlert('error', transferError);
          return;
        }
      }

      // Step 2: Update helmet information using helmetInfo
      let putHelmetError = '';
      try {
        // helmetInfo is the HelmetInfo object set at lookup/disambiguation
        const helmetToUpdate = {
          rfidBarcode: helmetInfo.rfidBarcode || '',
          playerUnitSerialString: helmetInfo.playerUnitSerialString,
          helmetRFIDLabel: helmetInfo.helmetRFIDLabel || '',
          originalMaterialNo: helmetInfo.originalMaterialNo || '',
          helmetHatSize: helmetInfo.helmetHatSize || null,
          features: helmetInfo.features ?? '',
          shell: helmetInfo.shell || '',
          shellColor: helmetInfo.shellColor || '',
          colorCode: helmetInfo.colorCode || '',
          paintCode1: helmetInfo.paintCode1 || '',
          paintCode1Description: helmetInfo.paintCode1Description || '',
          paintCode2: helmetInfo.paintCode2 || '',
          paintCode2Description: helmetInfo.paintCode2Description || '',
          helmetType: helmetInfo.helmetType || '',
          helmetColor: helmetInfo.helmetColor || '',
          paintDesignCode: helmetInfo.paintDesignCode || '',
          paintDesignDescription: helmetInfo.paintDesignDescription || '',
          helmet_config_id: helmetInfo.helmet_config_id ?? null
        };
        // Validate helmetToUpdate
        if (!helmetToUpdate.playerUnitSerialString) {
          logger.error('Invalid helmetToUpdate payload', { helmetToUpdate });
          setError('Invalid helmetToUpdate payload');
          return;
        }
        await API.PutHelmet(helmetToUpdate as any, helmetInfo.playerUnitSerialString);
      } catch (err: any) {
        putHelmetError = 'Helmet update failed after player unit was moved to new team: ' + (err?.message || err);
        logger.error(putHelmetError, { error: err, helmetInfo });

        // Rollback: move the player unit back to its original team
        if (!isConvertibleHelmet && helmetInfo.sourceTeamID) {
          try {
            const rollbackPayload = {
              playerUnitSerialString: helmetInfo.playerUnitSerialString,
              playerUnitFirmwareVersion: helmetInfo.playerUnitFirmwareVersionSemVer || '',
              playerUnitHardwareNumber: helmetInfo.playerUnitHardwareNumber,
              playerUnitAddress: 0,
              slot: 0,
              superFrame: 0,
              teamID: helmetInfo.sourceTeamID,
            };
            await API.PostPlayerUnitToTeam(rollbackPayload);
            logger.info('Rollback successful: player unit returned to original team', { sourceTeamID: helmetInfo.sourceTeamID });
            setError(
              'The helmet metadata update failed, so the transfer was rolled back. ' +
              'The player unit has been returned to its original team. ' +
              'Please try the transfer again or contact support if the issue persists.'
            );
            addAlert('error',
              'Transfer rolled back — helmet metadata update failed. The player unit was returned to its original team.'
            );
          } catch (rollbackErr: any) {
            logger.error('Rollback failed: could not return player unit to original team', {
              error: rollbackErr,
              helmetInfo,
              sourceTeamID: helmetInfo.sourceTeamID
            });
            setError(
              'CRITICAL: The helmet metadata update failed and the rollback also failed. ' +
              'The player unit (S/N: ' + helmetInfo.playerUnitSerialString + ') was moved to the new team but could not be returned to its original team. ' +
              'Please manually reassign it to the original team or contact support immediately.'
            );
            addAlert('error',
              'CRITICAL: Transfer partially failed and rollback failed. Manual intervention required for player unit S/N: ' + helmetInfo.playerUnitSerialString,
              30000
            );
          }
        } else {
          // No source team ID available — cannot roll back
          setError(
            'The helmet metadata update failed after the player unit was moved to the new team. ' +
            'The original team could not be determined for rollback. ' +
            'Please verify the player unit (S/N: ' + helmetInfo.playerUnitSerialString + ') is assigned to the correct team.'
          );
          addAlert('error', 'Transfer partially failed — helmet update error. Please verify team assignment for player unit S/N: ' + helmetInfo.playerUnitSerialString, 30000);
        }
        return;
      }

      // Step 4: Success UI and cleanup
      const targetOrg = organizations.find(org => org.orgID === parseInt(data.targetOrgID));
      const targetTeam = availableTargetTeams.find(team => team.teamID === parseInt(data.targetTeamID || '0'));

      const sourceDisplay = helmetInfo.sourceOrgName || 'source organization';
      const targetDisplay = targetOrg ? (targetTeam ? `${targetOrg.orgName} - ${targetTeam.teamName}` : targetOrg.orgName) : 'target organization';
      const helmetDisplay = helmetInfo.helmetRFIDLabel || helmetInfo.rfidBarcode || helmetInfo.helmetId;

      setTransferSuccess({
        helmetDisplay,
        sourceDisplay,
        targetDisplay
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
      addAlert('success', `Helmet ${helmetDisplay} successfully transferred from ${sourceDisplay} to ${targetDisplay}`);

      resetForm();
      resetLookupState();
    } catch (error: any) {
      logger.error('Helmet lookup transfer failed', { module: 'HelmetLookupTool', data: { error: error.message } });
      setError('Failed to transfer helmet. Please try again.');
      addAlert('error', 'Helmet transfer failed');
    } finally {
      setIsSubmitting(false);
    }
  }

  const resetTransferState = () => {
    setTransferSuccess(null)
    setIsSubmitting(false)
  }

  return {
    isSubmitting,
    transferSuccess,
    handleTransferSubmit,
    resetTransferState,
    setTransferSuccess
  }
}
